import { createLogic, StandardAction } from "redux-logic";
import * as UtilMethods from "../../../utils/UtilMethods";
import * as CartUtility from "../../../utils/CartUtility";
/**
 * -----------------------------
 *  Action Creator Imports
 * -----------------------------
 */
import {
  SET_CART_DATA,
  SCAN_CART_ID,
  CartState,
  FETCH_CART_DATA,
  HANDLE_CART_FAILURE_RESPONSE,
} from "../types/CartTypes";

import {
  ISortCartResultModel,
  SortCartValidationResult,
} from "../../interfaces/SortCart/ISortCartResultModel";
import { IPayloadWrapper, PayloadWrapper } from "../types/PayloadWrapper";
import {
  setCartID,
  setCartData,
  setForceCart,
  handleCartFailureResponse,
} from "../reducers/cartReducer";
import {
  setError,
  setWasCancelled,
  setForceShelf,
  setLoadingStatus,
  setIsScanIdValidationRxRequired,
  setIsScanIdValidationRemoveSkuRequired,
  setIsScanIdValidationCancelSortboxRequired,
} from "../reducers/applicationStateReducer";
import { setCurrentInstruction, setWarningModal } from "../reducers/instructionStateReducer";
import { ErrorModel, IErrorModel } from "../../interfaces/Error/IErrorModel";
import { InstructionState } from "../types/InstructionStateTypes";
import CartEndpoints from "../../cart/CartEndpoints";
/**
 * -----------------------------
 *  Redux Logic Definitions
 * -----------------------------
 */
const cartScanLogic = createLogic({
  type: SCAN_CART_ID,
  validate({ action }: { action: StandardAction<string, IPayloadWrapper<String>> }, allow, reject) {
    const cartNumber = UtilMethods.ParseCartNumberFromScan(action.payload.value);
    if (isNaN(cartNumber) || cartNumber <= 0) {
      reject(action);
    } else {
      allow(setCartID(new PayloadWrapper(cartNumber)));
    }
  },
});

const fetchCartDataLogic: any = createLogic({
  type: FETCH_CART_DATA,
  validate(
    {
      getState,
      action,
    }: {
      getState: () => any;
      action: StandardAction<
        string,
        { setCart: () => void; showInstruction: () => void; showWarningModal: () => void }
      >;
    },
    allow,
    reject
  ) {
    if (getState().cartReducer.currentCart > 0) {
      allow(action);
    } else {
      reject(setLoadingStatus(new PayloadWrapper(false)));
    }
  },
  process(
    {
      getState,
      action,
    }: {
      getState: any;
      action: StandardAction<
        string,
        { setCart: () => void; showInstruction: () => void; showWarningModal: () => void }
      >;
    },
    dispatch,
    done
  ) {
    const cartState: CartState = getState().cartReducer;
    const cartNumber: number = cartState.currentCart;
    const instructionState: InstructionState = getState().instructionReducer;
    const previousInstructionID: number = instructionState.previousInstructionID;
    const forceCart: boolean = cartState.forceCart;
    CartEndpoints.setCart(cartNumber, forceCart, previousInstructionID)
      .then((cartData: ISortCartResultModel) => {
        if (cartData.validationResult === SortCartValidationResult.OK) {
          dispatch(setCartData(cartData, action.payload));
          dispatch(setForceShelf(new PayloadWrapper(-1)));
          dispatch(setForceCart(new PayloadWrapper(false)));
          dispatch(setWasCancelled(new PayloadWrapper(false)));
          dispatch(
            setIsScanIdValidationRxRequired(
              new PayloadWrapper(cartData.isScanIdValidationRxRequired)
            )
          );
          dispatch(
            setIsScanIdValidationRemoveSkuRequired(
              new PayloadWrapper(cartData.isScanIdValidationRemoveSkuRequired)
            )
          );
          dispatch(
            setIsScanIdValidationCancelSortboxRequired(
              new PayloadWrapper(cartData.isScanIdValidationCancelSortboxRequired)
            )
          );
          done();
        } else {
          dispatch(
            handleCartFailureResponse(cartData, {
              showInstruction: action.payload.showInstruction,
              showWarningModal: action.payload.showWarningModal,
            })
          );
          dispatch(setLoadingStatus(new PayloadWrapper(false)));
          done();
        }
      })
      .catch((err: IErrorModel) => {
        dispatch(setCartID(new PayloadWrapper(-1)));
        dispatch(setError(err));
        done();
      });
  },
});

const cartDataLogic = createLogic({
  type: SET_CART_DATA,
  transform(
    {
      action,
    }: {
      getState: any;
      action: StandardAction<string, ISortCartResultModel, { setCart: () => void }>;
    },
    next
  ) {
    const cartType = UtilMethods.getCartType(
      false
    );
    action.payload.cartType = cartType;
    next(action);
  },
  process(
    {
      action,
    }: {
      getState: any;
      action: StandardAction<string, ISortCartResultModel, { setCart: Function }>;
    },
    dispatch,
    done
  ) {
    action.meta.setCart();
    dispatch(setLoadingStatus(new PayloadWrapper(false)));
    done();
  },
});

const handleSetCartFailureResponseLogic = createLogic({
  type: HANDLE_CART_FAILURE_RESPONSE,
  process(
    {
      getState,
      action,
    }: {
      getState: any;
      action: StandardAction<
        string,
        ISortCartResultModel,
        { showInstruction: () => void; showWarningModal: () => void }
      >;
    },
    dispatch,
    done
  ) {
    const cartData = action.payload;
    const cartNumber = getState().cartReducer.currentCart;
    const cartResult = CartUtility.SetCartValidationResult(cartData);
    action.payload.validationResult = cartResult;

    let callback: StandardAction<string>;

    if (cartResult === SortCartValidationResult.HasInstruction) {
      cartData.instruction!.visible = true;
      callback = setCurrentInstruction(cartData.instruction, action.meta.showInstruction);
    } else if (cartResult === SortCartValidationResult.UnauthorizedCompany) {
      const validationWarning = CartUtility.getCartValidationResultWarning(cartData);
      callback = setWarningModal(validationWarning, action.meta.showWarningModal);
    } else {
      const err = new ErrorModel();
      err.message = `Kunde inte hämta vagn ${cartNumber}`;
      callback = setError(err);
    }
    dispatch(setCartID(new PayloadWrapper(-1)));
    dispatch(callback);
    done();
  },
});

export { cartScanLogic, cartDataLogic, fetchCartDataLogic, handleSetCartFailureResponseLogic };
